import { BlockEditorInput } from "@babackapp/blocks-editor/block-editor-input";
import { useVariablesDictionary } from "@babackapp/editor-blocks-library/variables-context";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import { FloatingPanelContent, FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import { FormField } from "@babackapp/ui/components/form";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useEditorHistoryContext } from "@babackapp/blocks-editor/contexts/editor-history-context";
import { useEmailEditorLocale } from "../../context/templates-editor-locale-context";
import { useTemplatesEditorConfig } from "../../context/templates-editor-config-context";
import { useTemplatesEditorTemplates } from "../../context/templates-editor-templates-context";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { TranslatableField } from "@babackapp/ui/components/translatable-field";

import DependantWorkflows from "../dependant-workflows";
import { EmailTemplateFormData } from "../../types";


export default function LeftSidebarEditionPanel() {
  const { t } = useTranslation("trackoffice");
  const { control, watch, setValue } = useFormContext<EmailTemplateFormData>();
  const { showWorkflowDependencies, subjectManagedByWorkflow } =
    useTemplatesEditorConfig();
  const { currentTemplate } = useTemplatesEditorTemplates();
  const templateType = watch("type") || "email";

  const subject = watch("subject");
  const { variables } = useVariablesDictionary();
  const { localeCodes, currentLocale } = useEmailEditorLocale();
  const { captureState } = useEditorHistoryContext();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return (
    <FloatingPanelContent>
      {showWorkflowDependencies !== false && (
        <DependantWorkflows templateId={currentTemplate?.id} trigger="label" />
      )}
      <div className="flex gap-2 flex-col">
        <ContextualTipsTrigger
          wrapped
          tips={[
            {
              id: "template-title",
              title: t(
                "templatesEditor.tips.templateTitle.title",
                "Template's title",
              ),
              description: t(
                "templatesEditor.tips.templateTitle.description",
                "Name your template so you can easily identify it in the list of templates. This will never be shown to your users.",
              ),
            },
          ]}
        >
          <FormField
            control={control}
            name={`title`}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "notificationsTemplatesEditor.emailEditor.header.templateNameTitle",
                    `Template name`,
                  )}
                </span>
                <FormInput
                  label=""
                  value={field.value as string}
                  onChange={(value) => {
                    field.onChange(value);
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    debounceTimerRef.current = setTimeout(() => {
                      captureState();
                    }, 300);
                  }}
                  placeholder={t(
                    "notificationsTemplatesEditor.emailEditor.header.templateNameTitlePlaceholder",
                    `Give a name to this template...`,
                  )}
                  className="text-sm"
                />
              </div>
            )}
          />
        </ContextualTipsTrigger>
        {/*
         * v2 (useTemplatesAndWorkflowsV2): the subject is defined per workflow,
         * not on the template, so we hide the template-level subject field.
         */}
        {!subjectManagedByWorkflow && (
          <>
            <FloatingPanelSeparator />
            <ContextualTipsTrigger
              wrapped
              tips={[
                {
                  id: "template-subject",
                  title: t(
                    "templatesEditor.tips.templateSubject.title",
                    "Template's subject",
                  ),
                  description: t(
                    "templatesEditor.tips.templateSubject.description",
                    "You can customize the subject of the email using variables. Don't forget to translate the subject in the language of your users!",
                  ),
                },
              ]}
            >
              <div className="flex flex-col gap-1">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {templateType === "email"
                    ? t(
                      "notificationsTemplatesEditor.emailEditor.header.subjectTitle",
                      `Default subject`,
                    )
                    : t(
                      "notificationsTemplatesEditor.emailEditor.header.titleTitle",
                      "Default title",
                    )
                  }
                </span>
                <TranslatableField
                  value={subject as Record<string, string>}
                  onChange={(value) => {
                    setValue("subject", value, { shouldDirty: true });
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    debounceTimerRef.current = setTimeout(() => {
                      captureState();
                    }, 300);
                  }}
                  locales={localeCodes}
                  defaultLocale={currentLocale}
                  placeholder={t(
                    "notificationsTemplatesEditor.emailEditor.header.subjectPlaceholder",
                    `Enter email subject...`,
                  )}
                  className="text-sm"
                  renderInput={({ value, onChange, placeholder }) => (
                    <BlockEditorInput
                      variables={variables}
                      value={value}
                      onChange={onChange}
                      placeholder={placeholder}
                      outputFormat="text"
                      className="pr-16"
                    />
                  )}
                />
              </div>
            </ContextualTipsTrigger>
          </>
        )}
        {/**
         * Note before enabling the preview field:
         * - The templates preview is currently not used when sending emails (default templates preview is used instead).
         * - Before using templates preview, we MUST ensure that the existing templates have a proper preview set (not the case for most shop because of broken variables)
         **/}
        <div className="hidden">
          <FormField
            control={control}
            name="preview"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                  {t(
                    "notificationsTemplatesEditor.emailEditor.header.previewTitle",
                    `Default preview`,
                  )}
                </span>
                <TranslatableField
                  value={field.value as Record<string, string>}
                  onChange={field.onChange}
                  locales={localeCodes}
                  defaultLocale={currentLocale}
                  placeholder={t(
                    "notificationsTemplatesEditor.emailEditor.header.previewPlaceholder",
                    `Enter email preview...`,
                  )}
                  className="text-sm"
                  renderInput={({ value, onChange, placeholder }) => (
                    <BlockEditorInput
                      variables={variables}
                      value={value}
                      onChange={onChange}
                      placeholder={placeholder}
                      outputFormat="text"
                      className="pr-16"
                    />
                  )}
                />
              </div>
            )}
          />
        </div>
      </div>
    </FloatingPanelContent>
  );
}
